/** Compare bubble flow endpoints to skip redundant React state updates. */
export function greenEnergyFlowsNearlyEqual(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const keys = ['sx', 'sy', 'dx', 'dy', 'leg1x', 'leg1y', 'leg2x', 'leg2y'];
  return keys.every(key => Math.abs((a[key] ?? 0) - (b[key] ?? 0)) < 1);
}

/** Fixed-position bubble path from one icon center to another (viewport px). */
export function measureGreenEnergyIconFlowPx(fromEl, toEl, { fromEdge = 'center' } = {}) {
  if (!fromEl || !toEl) return null;
  const from = fromEl.getBoundingClientRect();
  const to = toEl.getBoundingClientRect();
  const sx = from.left + from.width / 2;
  const sy = fromEdge === 'top' ? from.top : fromEdge === 'bottom' ? from.bottom : from.top + from.height / 2;
  const tx = to.left + to.width / 2;
  const ty = to.top + to.height / 2;
  return { sx, sy, dx: tx - sx, dy: ty - sy };
}

/**
 * L-shaped route below carousel cards: down → across bottom → up to target icon.
 * Keeps flow under the main card (slider / pay button).
 */
export function measureGreenEnergyBottomRouteFlowPx(
  fromEl,
  toEl,
  obstacleEl,
  { clearancePx = 14, fromEdge = 'bottom' } = {}
) {
  if (!fromEl || !toEl) return null;
  const from = fromEl.getBoundingClientRect();
  const to = toEl.getBoundingClientRect();
  const obstacle = obstacleEl?.getBoundingClientRect?.();

  const sx = from.left + from.width / 2;
  const sy = fromEdge === 'top' ? from.top : from.bottom;
  const tx = to.left + to.width / 2;
  const ty = to.top + to.height / 2;

  const routeY = Math.max(from.bottom, to.bottom, obstacle?.bottom ?? 0) + clearancePx;
  const leg1y = routeY - sy;
  const leg2x = tx - sx;
  const leg2y = routeY - sy;

  return {
    sx,
    sy,
    dx: tx - sx,
    dy: ty - sy,
    leg1x: 0,
    leg1y,
    leg2x,
    leg2y,
  };
}

export const GREEN_ENERGY_FLOW_BUBBLE_COUNT = 5;

export function greenEnergyFlowBubbleStyle(flowPx, index, { invertDx = false, bottomRoute = false } = {}) {
  const dxJitter = ((index % 5) - 2) * 6;
  const dyJitter = ((index % 4) - 1.5) * 5;
  const style = {
    left: `${flowPx.sx}px`,
    top: `${flowPx.sy}px`,
    animationDelay: `${index * 0.52}s`,
  };

  if (bottomRoute) {
    style['--gf-leg1x'] = `${flowPx.leg1x ?? 0}px`;
    style['--gf-leg1y'] = `${flowPx.leg1y ?? 0}px`;
    style['--gf-leg2x'] = `${flowPx.leg2x ?? flowPx.dx}px`;
    style['--gf-leg2y'] = `${flowPx.leg2y ?? flowPx.leg1y ?? 0}px`;
    style['--gf-dx'] = `${flowPx.dx + (invertDx ? -dxJitter : dxJitter)}px`;
    style['--gf-dy'] = `${flowPx.dy + dyJitter}px`;
    return style;
  }

  return {
    ...style,
    '--gf-dx': `${flowPx.dx + (invertDx ? -dxJitter : dxJitter)}px`,
    '--gf-dy': `${flowPx.dy + dyJitter}px`,
  };
}
